import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useBillingMoney, useBillingPlans, useChangeBuildingPlan } from "../hooks";
import { billingErrorMessage } from "../messages";
import type { BillingContext, BuildingPlanTier, BuildingSubscription } from "../types";
import { BuildingPlanChoice } from "./BuildingPlanChoice";
import { BuildingPlanRestriction } from "./BuildingPlanRestriction";
import { paidBuildingCreationBlock, paidBuildingSetupNeeded } from "../buildingPlanCreation";

export function ChangeBuildingPlanDialog({
  context,
  subscription,
  name,
  legacyRateCents,
  onClose,
}: {
  context: BillingContext;
  subscription: BuildingSubscription;
  name: string;
  legacyRateCents?: number;
  onClose: () => void;
}) {
  const { t } = useLanguage();
  const plans = useBillingPlans();
  const money = useBillingMoney();
  const mutation = useChangeBuildingPlan();
  const [tier, setTier] = useState<BuildingPlanTier>(subscription.plan.code);
  const currentRateCents =
    subscription.billing_enabled === false
      ? (legacyRateCents ?? context.effective_rate_cents)
      : subscription.plan.rate_cents;
  const reason =
    !context.is_org_owner || !subscription.can_manage
      ? t.billing.restrictions.ownerOnly
      : (paidBuildingCreationBlock(context, tier, t) ??
        (subscription.requested_tier ? t.billing.plans.pendingHelp : undefined));
  const submit = async () => {
    try {
      const result = await mutation.mutateAsync({
        orgId: context.org_id,
        buildingId: subscription.building_id,
        tier,
      });
      toast.success(
        result.status === "pending" ? t.billing.plans.pendingHelp : t.billing.plans.changed
      );
      onClose();
    } catch (error) {
      toast.error(billingErrorMessage(error, t, t.billing.plans.changeFailed));
    }
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !mutation.isPending) onClose();
      }}
    >
      <DialogContent className="max-w-md" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>
            {t.billing.plans.change}: {name}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {t.billing.plans.summary.currentRate}{" "}
          <strong className="font-semibold text-foreground">
            {money(currentRateCents, context.currency)} {t.billing.plans.summary.perMonth}
          </strong>
          .
        </p>
        {plans.isLoading ? (
          <p>{t.billing.consent.loading}</p>
        ) : plans.isError || !plans.data ? (
          <div className="space-y-2">
            <p className="text-sm text-destructive">{t.billing.plans.unavailable}</p>
            <Button variant="outline" onClick={() => void plans.refetch()}>
              {t.billing.errors.retry}
            </Button>
          </div>
        ) : (
          <BuildingPlanChoice
            plans={plans.data}
            tier={tier}
            onChange={setTier}
            disabled={mutation.isPending}
            presentation={{ kind: "change" }}
          />
        )}
        <BuildingPlanRestriction
          reason={reason}
          needsBillingSetup={subscription.can_manage && paidBuildingSetupNeeded(context, tier)}
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            {t.billing.consent.cancel}
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={
              mutation.isPending ||
              Boolean(reason) ||
              !plans.data ||
              plans.isError ||
              (tier === subscription.plan.code && subscription.billing_enabled !== false)
            }
          >
            {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {t.billing.plans.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
